import classNames from "classnames";
import { useMemo, useRef, useState } from "react";
import { Button } from "../../../components/buttons/button";
import { EmptyState } from "../../../components/empty";
import {
  FindEventListItem,
  FindEventListItemLoader,
} from "../../../components/event/findEventListItem";
import {
  TrendingCardLoader,
  TrendingEventCard,
} from "../../../components/event/trendingEventCard";
import { Icon } from "../../../components/icon/icon";
import { SearchInput } from "../../../components/inputs/searchInput";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useFetchAvailableCategoriesQuery,
  useFindEventsQuery,
} from "../../../redux/features/event/event.api";
import { FindEventsQuery } from "../../../redux/features/event/event.types";
import { FindEventsCategoryOptions } from "../../../utils/constants";
import { DateFilter } from "./components/dateFilter";
import { LocationFilter } from "./components/locationFilter";
import { PriceFilter } from "./components/priceFilter";
import { getEarliestTrendingEvents } from "./trendingEvents";

const findEventLimit = 10;
const trendingCarouselLimit = 5;

export const FindEventsPage = ({
  pageScrollProgress,
  mobileSearchOpen,
  toggleMobileSearch,
}: {
  navbarHeight: number;
  pageScrollProgress: number;
  mobileSearchOpen: boolean;
  toggleMobileSearch: () => void;
}) => {
  const [query, setQuery] = useState<FindEventsQuery>({
    page: 1,
    limit: findEventLimit,
    search: undefined,
    sortOrder: "asc",
    location: undefined,
    category: undefined,
    price: undefined,
    date: undefined,
    startDate: undefined,
    endDate: undefined,
    isTrending: undefined,
    country: undefined,
  });
  const trendingRailRef = useRef<HTMLDivElement>(null);

  const {
    data: events,
    isFetching: fetchingEvents,
    isError: eventsFetchError,
    refetch: refetchEvents,
  } = useFindEventsQuery(
    {
      ...query,
      search: query.search === "" ? undefined : query.search,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: trendingEvents, isFetching: fetchingTrendingEvents } =
    useFindEventsQuery(
      {
        page: 1,
        limit: 20,
        isTrending: true,
        sortOrder: "asc",
        country: query.country,
        location: query.location,
      },
      { refetchOnMountOrArgChange: true },
    );

  const { data: availableCatefories, isFetching: fetchingAvailableCategories } =
    useFetchAvailableCategoriesQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const earliestTrendingEvents = useMemo(() => {
    return getEarliestTrendingEvents(trendingEvents?.data?.data ?? []);
  }, [trendingEvents?.data?.data]);

  const hasTrendingEvents = !["", 0, undefined].includes(
    earliestTrendingEvents.length,
  );

  const categoryFilterRow = (
    <div className="-my-4 flex w-fit max-w-full flex-row gap-[10px] py-4">
      {FindEventsCategoryOptions.filter(
        (c) =>
          c.value === "free" ||
          c.value === undefined ||
          (fetchingAvailableCategories
            ? true
            : availableCatefories?.data?.includes(c.value!)),
      ).map((cat, idx) => {
        const isActive =
          ((query.category === cat.value && query.price === undefined) ||
            (query.price === cat.value && query.category === undefined)) &&
          !fetchingAvailableCategories;

        return (
          <BgWrapper
            loading={fetchingAvailableCategories}
            loaderHeight={20}
            key={`category-${idx}`}
            className={classNames(
              "soft-shrink flex items-center gap-1.5 !rounded-full !border !border-white/80 !bg-white/75 !py-2 !pl-2.5 !pr-3.5 shadow-[0px_8px_24px_rgba(9,21,37,0.06)] backdrop-blur-xl",
              "cursor-pointer whitespace-nowrap font-onest text-sm font-medium text-ink transition-colors duration-300",
              {
                "!w-[70px]": fetchingAvailableCategories,
                "!border-[#A6E2F4] outline outline-1 outline-[#292929]":
                  isActive,
              },
            )}
            onClick={() => {
              if (cat.value === "free") {
                setQuery({
                  ...query,
                  page: 1,
                  limit: findEventLimit,
                  category: undefined,
                  price: "free",
                  date: undefined,
                  startDate: undefined,
                  endDate: undefined,
                });
              } else {
                setQuery({
                  ...query,
                  page: 1,
                  limit: findEventLimit,
                  category: cat.value,
                  price: undefined,
                  date: undefined,
                  startDate: undefined,
                  endDate: undefined,
                });
              }
            }}
          >
            <Icon
              svg={cat.icon}
              className={classNames(cat.iconClassName, {
                "!text-ink": isActive,
              })}
              width={16}
              height={16}
            />
            <p>{cat.text}</p>
          </BgWrapper>
        );
      })}
    </div>
  );

  const searchResultsCount =
    !fetchingEvents && query.search && (events?.data?.totalCount ?? 0) > 0 ? (
      <p className="text-base font-semibold text-grey-800">
        {events?.data?.totalCount}{" "}
        {events?.data?.totalCount === 1 ? "event" : "events"} found for '
        {query.search}'
      </p>
    ) : undefined;

  const showMoreButton = (
    <div
      className={classNames("mt-14 flex w-full items-center justify-center", {
        hidden:
          events?.data?.data?.length === events?.data?.totalCount ||
          ["", 0, undefined].includes(events?.data?.data?.length) ||
          fetchingEvents,
      })}
    >
      <Button
        type="button"
        text="Show More Events"
        variation="plain-grey"
        loading={fetchingEvents}
        onClick={() => {
          setQuery({
            ...query,
            limit: query.limit! + findEventLimit,
          });
        }}
        className="glossy-button max-w-fit !px-6 !py-4"
      />
    </div>
  );

  const eventsErrorState =
    !fetchingEvents && eventsFetchError ? (
      <EmptyState
        text="Events Couldn’t Load"
        subtext="We couldn’t reach the event service. Please try again shortly."
        className="w-full !bg-transparent"
        image="/assets/images/search-glass.png"
        ctaText="Retry"
        onCTAClick={() => {
          refetchEvents();
        }}
      />
    ) : undefined;

  const eventsEmptyState =
    !fetchingEvents &&
    !eventsFetchError &&
    ["", 0, undefined].includes(events?.data?.data?.length) ? (
      <EmptyState
        text="No Events Found"
        subtext="There are no events that meet your seach"
        className="w-full !bg-transparent"
        image="/assets/images/search-glass.png"
      />
    ) : undefined;

  return (
    <div className="relative overflow-hidden bg-[#F4F7FA] pb-14">
      <div
        className={classNames("relative z-[2] pb-2 pt-2 md:pb-7 md:pt-2", {
          "border-b border-white/80 bg-white/75 shadow-[0px_12px_36px_rgba(9,21,37,0.08)] backdrop-blur-xl":
            pageScrollProgress > 0.1,
        })}
      >
        {/* Filters */}
        <div className="max-width-website flex w-full flex-row">
          <LocationFilter query={query} setQuery={setQuery} />
          <div className="flex w-full justify-end gap-3">
            <PriceFilter query={query} setQuery={setQuery} />
            <DateFilter query={query} setQuery={setQuery} />
          </div>
        </div>
      </div>

      <div className="max-width-website relative z-[1] flex w-full flex-col gap-12 pb-10 pt-4 md:gap-16 md:pt-2">
        {mobileSearchOpen ? (
          <div className="flex items-center gap-3 md:hidden">
            <SearchInput
              placeholder="Search events"
              searchFunction={(searchVal) => {
                setQuery({
                  ...query,
                  page: 1,
                  search: searchVal,
                  limit: findEventLimit,
                });
              }}
              value={query.search}
              inputClassName="!rounded-full !border-none"
              iconClassName="text-grey-600"
              className="!w-full !rounded-full !border !border-grey-200 !bg-white"
              debouncedSearch={false}
              customElement={
                <Icon
                  svg="arrow-right"
                  width={24}
                  height={24}
                  className="cursor-pointer rounded-full bg-ink p-[5px] text-white"
                />
              }
            />

            <button
              type="button"
              aria-label="Close search"
              onClick={() => {
                toggleMobileSearch();
                setQuery({ ...query, page: 1, search: undefined });
              }}
            >
              <Icon
                svg="close-icon"
                width={20}
                height={20}
                className="text-grey-600"
              />
            </button>
          </div>
        ) : undefined}

        {hasTrendingEvents || fetchingTrendingEvents ? (
          <div className="relative">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <h2 className="font-dela text-[20px] leading-[1.15] text-ink md:text-[32px]">
                  Trending now
                </h2>
              </div>

              <div className="hidden items-center gap-2 md:flex">
                <button
                  type="button"
                  aria-label="Scroll trending events left"
                  onClick={() => {
                    trendingRailRef.current?.scrollBy({
                      left: -360,
                      behavior: "smooth",
                    });
                  }}
                  className="shadow-button flex h-10 w-10 items-center justify-center rounded-full border border-white/80 bg-white/85 text-ink backdrop-blur-xl transition-all duration-300 hover:-translate-x-0.5 md:h-11 md:w-11"
                >
                  <Icon
                    svg="arrow-right"
                    width={21}
                    height={21}
                    className="rotate-180"
                  />
                </button>
                <button
                  type="button"
                  aria-label="Scroll trending events right"
                  onClick={() => {
                    trendingRailRef.current?.scrollBy({
                      left: 360,
                      behavior: "smooth",
                    });
                  }}
                  className="shadow-button flex h-10 w-10 items-center justify-center rounded-full border border-white/80 bg-white/85 text-ink backdrop-blur-xl transition-all duration-300 hover:translate-x-0.5 md:h-11 md:w-11"
                >
                  <Icon svg="arrow-right" width={21} height={21} />
                </button>
              </div>
            </div>

            <div className="relative">
              <div
                ref={trendingRailRef}
                className="overflow-x-scroll py-5 md:py-7"
              >
                <div className="relative grid w-max auto-cols-max grid-flow-col gap-4 px-1 md:gap-5">
                  {fetchingTrendingEvents ? (
                    <>
                      {[1, 2, 3, 4, 5].map((idx) => (
                        <TrendingCardLoader key={`${idx}`} />
                      ))}
                    </>
                  ) : undefined}

                  {!fetchingTrendingEvents && hasTrendingEvents ? (
                    <>
                      {earliestTrendingEvents
                        .slice(0, trendingCarouselLimit)
                        .map((event, idx) => (
                          <TrendingEventCard
                            event={event}
                            key={`${event._id}-${idx}`}
                          />
                        ))}
                    </>
                  ) : undefined}
                </div>
              </div>
            </div>
          </div>
        ) : undefined}

        <div>
          <div className="flex w-full flex-col justify-between gap-4 lg:flex-row lg:items-end">
            <div>
              <h2 className="font-dela text-[20px] leading-[1.15] text-ink md:text-[32px]">
                {query.country ? `Events in ${query.country}` : "All Events"}
              </h2>
            </div>

            <SearchInput
              placeholder="Search events"
              searchFunction={(searchVal) => {
                setQuery({
                  ...query,
                  page: 1,
                  search: searchVal,
                  limit: findEventLimit,
                });
              }}
              value={query.search}
              inputClassName="!rounded-full !border-none"
              iconClassName="text-grey-600"
              className="!max-w-full !rounded-full !border !border-white/80 !bg-white/80 shadow-[0px_10px_30px_rgba(9,21,37,0.08)] backdrop-blur-xl lg:!max-w-[360px]"
              debouncedSearch={false}
              customElement={
                <Icon
                  svg="arrow-right"
                  width={24}
                  height={24}
                  className="cursor-pointer rounded-full bg-ink p-[5px] text-white"
                />
              }
            />
          </div>

          <div className="mt-6 md:mt-8">{categoryFilterRow}</div>

          {searchResultsCount ? (
            <div className="mt-4">{searchResultsCount}</div>
          ) : undefined}

          <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-5 md:mt-10 md:grid-cols-2">
            {(fetchingEvents && query.limit! === findEventLimit) ||
            ["", 0, undefined].includes(
              events?.data?.data?.length,
            ) ? undefined : (
              <>
                {events?.data?.data?.map((event, idx) => {
                  return (
                    <FindEventListItem
                      event={event}
                      key={`${event._id}-${idx}`}
                    />
                  );
                })}
              </>
            )}

            {fetchingEvents ? (
              <>
                {[1, 2, 3, 4].map((idx) => (
                  <FindEventListItemLoader key={`${idx}`} />
                ))}
              </>
            ) : undefined}
          </div>

          {showMoreButton}
          {eventsErrorState}
          {eventsEmptyState}
        </div>
      </div>
    </div>
  );
};
